/**
 * @fileoverview Add-files dialog for a task (MOR task attachments).
 *
 * Purpose: the two ways a practitioner puts a document on a task — upload a
 * new file, or link one already in the job library. Mirrors the POD claim
 * `ClaimEvidenceDialog`: uploads go through the standard library upload
 * contract (which creates the `documents` row) and are then linked to the
 * task; linking picks an existing library document by id.
 *
 * Dependencies:
 * - useLibraryUpload (job upload contract, returns the new document ids)
 * - api.tasks.attachments.listTaskDocumentCandidates
 * - api.tasks.attachments.attachTaskDocument
 *
 * Used by:
 * - src/calendar/task-attachments-section.tsx
 */

import { useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { Check, FileText, Loader2, Upload } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import { Button } from "@/ui/primitives/button";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import { useLibraryUpload } from "@/components/jobs/phases/gather/library/useLibraryUpload";

export function TaskAttachmentsDialog({
  open,
  onOpenChange,
  keyDateId,
  jobId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keyDateId: Id<"jobKeyDates">;
  jobId: Id<"jobs">;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const { handleDrop, handlePickedFiles, uploadingCount } =
    useLibraryUpload(jobId);

  const attach = useMutation({
    mutationFn: useConvexMutation(api.tasks.attachments.attachTaskDocument),
  });

  const { data: candidates } = useQuery({
    ...convexQuery(api.tasks.attachments.listTaskDocumentCandidates, {
      keyDateId,
    }),
    enabled: open,
  });

  /** Links every uploaded document, reporting the first failure only. */
  async function attachAll(documentIds: Id<"documents">[]) {
    if (documentIds.length === 0) return;
    for (const documentId of documentIds) {
      await attach.mutateAsync({ keyDateId, documentId });
    }
    toast.success(
      documentIds.length === 1
        ? "File added to the task"
        : `${documentIds.length} files added to the task`,
    );
    onOpenChange(false);
  }

  async function runUpload(action: () => Promise<Id<"documents">[]>) {
    setBusy(true);
    try {
      await attachAll(await action());
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to add the file"));
    } finally {
      setBusy(false);
    }
  }

  async function linkExisting(documentId: Id<"documents">) {
    setBusy(true);
    try {
      await attach.mutateAsync({ keyDateId, documentId });
      toast.success("File added to the task");
      onOpenChange(false);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to add the file"));
    } finally {
      setBusy(false);
    }
  }

  const isWorking = busy || uploadingCount > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add files</DialogTitle>
          <DialogDescription>
            Upload a new file or link one already in the job library to this
            task.
          </DialogDescription>
        </DialogHeader>

        {/* `min-w-0`: DialogContent is a grid, whose items size to their
            max-content width by default — without this the document list
            stretches the panel past the dialog on long file names. */}
        <Tabs defaultValue="upload" className="min-w-0">
          <TabsList className="w-full">
            <TabsTrigger value="upload" className="flex-1 gap-1.5">
              <Upload className="h-3.5 w-3.5" /> Upload a file
            </TabsTrigger>
            <TabsTrigger value="existing" className="flex-1 gap-1.5">
              <FileText className="h-3.5 w-3.5" /> Link an existing file
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="mt-4">
            <button
              type="button"
              disabled={isWorking}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                const { dataTransfer } = e;
                void runUpload(() => handleDrop(dataTransfer));
              }}
              className={cn(
                "flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-6 py-10 text-center transition-colors",
                isDragOver
                  ? "border-primary bg-primary/5"
                  : "border-border hover:bg-muted/50",
                isWorking && "pointer-events-none opacity-60",
              )}
            >
              {isWorking ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : (
                <Upload className="h-5 w-5 text-muted-foreground" />
              )}
              <span className="text-sm font-medium">
                {isWorking
                  ? "Uploading…"
                  : "Drop a file here, or click to browse"}
              </span>
              <span className="text-xs text-muted-foreground">
                The file is added to the job library and linked to this task.
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                e.target.value = "";
                if (files.length > 0) {
                  void runUpload(() => handlePickedFiles(files));
                }
              }}
            />
          </TabsContent>

          <TabsContent value="existing" className="mt-4 min-w-0">
            <Command className="w-full rounded-lg border bg-transparent">
              <CommandInput placeholder="Search job documents..." />
              <CommandList className="max-h-72 overscroll-contain">
                <CommandEmpty>
                  {candidates === undefined
                    ? "Loading documents…"
                    : "No matching document."}
                </CommandEmpty>
                {(candidates ?? []).map((candidate) => (
                  <CommandItem
                    key={candidate.documentId}
                    value={candidate.fileName}
                    disabled={candidate.attachedHere || isWorking}
                    className="min-w-0"
                    onSelect={() => {
                      if (candidate.attachedHere) return;
                      void linkExisting(candidate.documentId);
                    }}
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span
                      className="min-w-0 flex-1 truncate"
                      title={candidate.fileName}
                    >
                      {candidate.fileName}
                    </span>
                    {candidate.attachedHere && (
                      <span className="flex shrink-0 items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-400">
                        <Check className="h-3 w-3" /> Added
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            disabled={isWorking}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
